import React, { useEffect, useRef, useState } from 'react';

import { ManageDialog } from 'components/Snippets/components/ManageDialog';
import { CreateDialog } from 'components/Snippets/components/CreateDialog';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import {
    fetchFolders,
    fetchSnippets,
    getAllFolders,
    getAllSnippets,
    getFolderStatus,
    getSnippetStatus,
    setSnippet,
} from 'features/snippets/snippetsSlice';
import { FormActions } from 'helpers';
import { Editor } from '@tinymce/tinymce-react';
import AddContentPlugin, { registerPlugin } from './plugins/AddContentPlugin';
import { DisplayTypeKeys } from 'GeminiViewerComponent/helpers';

const TinyMCEEditor = ({
    allowedDisplayTypes = [DisplayTypeKeys.Inline, DisplayTypeKeys.External, DisplayTypeKeys.CompanionApp],
    ...props
}) => {
    const dispatch = useDispatch();
    const editorRef = useRef(null);
    const draggedMediaRef = useRef(null); // { type: 'video'|'img', contentId: string, oldWrapper: HTMLElement|null }
    const lastEditorEmittedValue = useRef(null); // tracks last value emitted by editor to break controlled-value re-render loop
    const snippetsStatus = useSelector(getSnippetStatus);
    const foldersStatus = useSelector(getFolderStatus);
    const snippets = useSelector(getAllSnippets);
    const folders = useSelector(getAllFolders);
    const [createSnippet, setCreateSnippet] = useState(false);
    const [manageSnippet, setManageSnippet] = useState(null);
    const [hasContentPlugin, setHasContentPlugin] = useState(false);

    useEffect(() => {
        if (snippetsStatus === LoadingStatus.Idle) {
            dispatch(fetchSnippets());
        }
        if (foldersStatus === LoadingStatus.Idle) {
            dispatch(fetchFolders());
        }
    }, [dispatch, foldersStatus]);

    useEffect(() => {
        if (editorRef.current) {
            const editor = editorRef.current;
            insert_snippet(editor);
            snippet_shortcut(editor);
        }
    }, [snippets, folders]);

    const clear_content = (editor) => {
        editor.ui.registry.addMenuItem('clearcontent', {
            text: 'Clear Content',
            icon: 'new-document',
            onAction: async () => {
                await editor.focus();
                await editor.setContent('');
            },
        });
    };

    const insert_snippet = (editor) => {
        const getSnippets = (folder, editor) => {
            return snippets
                .filter((item) => item.snippet_folder_id === folder.snippet_folder_id)
                .map((snippet) => {
                    return {
                        type: 'menuitem',
                        text: snippet.display_name,
                        onAction: (e) => {
                            editor.isInsertingSnippet = true;
                            editor.insertContent(`${snippet.source}`);
                        },
                    };
                });
        };

        const getSnippetFolders = (editor) => {
            return folders.map((folder) => ({
                type: 'nestedmenuitem',
                text: folder.display_name,
                icon: 'folderIcon',
                getSubmenuItems: () => {
                    return getSnippets(folder, editor);
                },
            }));
        };

        editor.ui.registry.addMenuButton('insertSnippet', {
            tooltip: 'Insert Snippet',
            icon: 'insertSnippetIcon',
            fetch: (callback) => {
                callback(getSnippetFolders(editor));
            },
        });

        editor.ui.registry.addNestedMenuItem('insert', {
            text: 'Insert',
            icon: 'insertSnippetIcon',
            getSubmenuItems: () => getSnippetFolders(editor),
        });
    };

    const create_icons = (editor) => {
        editor.ui.registry.addIcon(
            'folderIcon',
            '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" height="1.1em" width="1.1em" xmlns="http://www.w3.org/2000/svg"><path d="M464 128H272l-64-64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V176c0-26.51-21.49-48-48-48z"></path></svg>'
        );

        editor.ui.registry.addIcon(
            'insertSnippetIcon',
            '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 24 24" height="1.65em" width="1.65em" xmlns="http://www.w3.org/2000/svg"><path fill="none" d="M0 0h24v24H0z"></path><path d="M20.41 8.41l-4.83-4.83c-.37-.37-.88-.58-1.41-.58H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9.83c0-.53-.21-1.04-.59-1.42zM7 7h7v2H7V7zm10 10H7v-2h10v2zm0-4H7v-2h10v2z"></path></svg>'
        );

        editor.ui.registry.addIcon(
            'pagebreakIcon',
            '<svg fill="#000000" height="24" width="24" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 408.105 408.105" xml:space="preserve"> <g><path d="M364.479,0c-4.143,0-7.5,3.357-7.5,7.5v159.924H91.065V7.5c0-4.143-3.358-7.5-7.5-7.5s-7.5,3.357-7.5,7.5v167.424 c0,4.143,3.358,7.5,7.5,7.5h280.914c4.143,0,7.5-3.357,7.5-7.5V7.5C371.979,3.357,368.621,0,364.479,0z"/><path d="M152.219,15h20c4.142,0,7.5-3.358,7.5-7.5c0-4.143-3.358-7.5-7.5-7.5h-20c-4.142,0-7.5,3.357-7.5,7.5 C144.719,11.642,148.077,15,152.219,15z"/><path d="M112.219,15h20c4.142,0,7.5-3.358,7.5-7.5c0-4.143-3.358-7.5-7.5-7.5h-20c-4.142,0-7.5,3.357-7.5,7.5 C104.719,11.642,108.077,15,112.219,15z"/><path d="M192.219,15h20c4.143,0,7.5-3.358,7.5-7.5c0-4.143-3.357-7.5-7.5-7.5h-20c-4.142,0-7.5,3.357-7.5,7.5 C184.719,11.642,188.077,15,192.219,15z"/><path d="M272.219,15h20c4.143,0,7.5-3.358,7.5-7.5c0-4.143-3.357-7.5-7.5-7.5h-20c-4.143,0-7.5,3.357-7.5,7.5 C264.719,11.642,268.076,15,272.219,15z"/><path d="M232.219,15h20c4.143,0,7.5-3.358,7.5-7.5c0-4.143-3.357-7.5-7.5-7.5h-20c-4.143,0-7.5,3.357-7.5,7.5 C224.719,11.642,228.076,15,232.219,15z"/><path d="M312.219,15h20c4.143,0,7.5-3.358,7.5-7.5c0-4.143-3.357-7.5-7.5-7.5h-20c-4.143,0-7.5,3.357-7.5,7.5 C304.719,11.642,308.076,15,312.219,15z"/><path d="M364.479,225.68H83.565c-4.142,0-7.5,3.357-7.5,7.5v167.426c0,4.143,3.358,7.5,7.5,7.5s7.5-3.357,7.5-7.5V240.68h265.914 v159.926c0,4.143,3.357,7.5,7.5,7.5s7.5-3.357,7.5-7.5V233.18C371.979,229.037,368.621,225.68,364.479,225.68z"/><path d="M252.824,393.105h-20c-4.143,0-7.5,3.357-7.5,7.5c0,4.143,3.357,7.5,7.5,7.5h20c4.143,0,7.5-3.357,7.5-7.5 C260.324,396.463,256.967,393.105,252.824,393.105z"/><path d="M292.824,393.105h-20c-4.143,0-7.5,3.357-7.5,7.5c0,4.143,3.357,7.5,7.5,7.5h20c4.143,0,7.5-3.357,7.5-7.5 C300.324,396.463,296.967,393.105,292.824,393.105z"/><path d="M332.824,393.105h-20c-4.143,0-7.5,3.357-7.5,7.5c0,4.143,3.357,7.5,7.5,7.5h20c4.143,0,7.5-3.357,7.5-7.5 C340.324,396.463,336.967,393.105,332.824,393.105z"/><path d="M132.824,393.105h-20c-4.142,0-7.5,3.357-7.5,7.5c0,4.143,3.358,7.5,7.5,7.5h20c4.142,0,7.5-3.357,7.5-7.5 C140.324,396.463,136.966,393.105,132.824,393.105z"/><path d="M212.824,393.105h-20c-4.142,0-7.5,3.357-7.5,7.5c0,4.143,3.358,7.5,7.5,7.5h20c4.143,0,7.5-3.357,7.5-7.5 C220.324,396.463,216.967,393.105,212.824,393.105z"/><path d="M172.824,393.105h-20c-4.142,0-7.5,3.357-7.5,7.5c0,4.143,3.358,7.5,7.5,7.5h20c4.142,0,7.5-3.357,7.5-7.5 C180.324,396.463,176.966,393.105,172.824,393.105z"/><path d="M81.573,204.053c0-2.348-1.1-4.561-2.971-5.979L48.156,175.01c-2.27-1.719-5.318-2.006-7.867-0.737 c-2.55,1.267-4.162,3.868-4.162,6.716v46.129c0,2.848,1.612,5.449,4.162,6.716c1.057,0.525,2.2,0.784,3.337,0.784 c1.606,0,3.201-0.516,4.53-1.521l30.446-23.064C80.474,208.613,81.573,206.4,81.573,204.053z M51.127,212.026v-15.947l10.526,7.974 L51.127,212.026z"/> </g> </svg>'
        );
    };

    const create_snippet = (editor) => {
        editor.ui.registry.addNestedMenuItem('create', {
            text: 'Create',
            icon: 'plus',
            getSubmenuItems: () => {
                return [
                    {
                        type: 'menuitem',
                        text: 'From Selection',
                        onAction: () => {
                            dispatch(
                                setSnippet({
                                    source: editor.selection.getContent(),
                                })
                            );
                            setCreateSnippet(true);
                        },
                    },
                    {
                        type: 'menuitem',
                        text: 'From Content',
                        onAction: () => {
                            dispatch(setSnippet({ source: editor.getContent() }));
                            setCreateSnippet(true);
                        },
                    },
                ];
            },
        });
    };

    const manage_snippet = (editor) => {
        editor.ui.registry.addMenuItem('manage', {
            text: 'Manage...',
            icon: 'edit-block',
            onAction: () => {
                setManageSnippet(true);
            },
        });
    };

    const snippet_shortcut = (editor) => {
        const handleTabKey = (editor) => {
            var sel = editor.selection.getSel();

            // inserted snippet HTML (including images) on every Tab press.
            if (!sel || sel.type === 'Caret') {
                return false;
            }

            var content = editor.selection.getContent({ format: 'text' }).toLowerCase();

            // Nothing selected – let Tab behave normally.
            if (!content || content.trim() === '') {
                return false;
            }

            const sortedSnippets = [...snippets.filter((x) => x.trigger_key !== undefined)];
            sortedSnippets.sort((a, b) => b.trigger_key.length - a.trigger_key.length);

            const snippet = sortedSnippets.find((text) => {
                let re = new RegExp('(^|\\W)' + text.trigger_key + '($|\\W)', 'g');
                return content.match(re);
            });

            if (snippet) {
                editor.isInsertingSnippet = true;
                editor.selection.setContent(snippet?.source);
                editor.selection.collapse(false);
                return true;
            }
            return false;
        };

        editor.on('keydown', (e) => {
            if (e.key === 'Tab') {
                if (handleTabKey(editor) == true) {
                    e.preventDefault();
                }
            }
        });
    };

    const onScriptsLoad = () => {
        registerPlugin(window.tinymce);
    };

    const onEditorInitialized = (e, editor) => {
        const target = e.target;
        setHasContentPlugin(!!target.plugins.addContent);
        target.serializer.addAttributeFilter('align', (nodes) => {
            for (let i = 0; i < nodes.length; i++) {
                if (nodes[i].name === 'img') {
                    const alignValue = nodes[i].attr('align');
                    if (alignValue) {
                        const styles = target.dom.styles.parse(nodes[i].attr('styles') || '');
                        styles['vertical-align'] = alignValue;
                        nodes[i].attr('style', target.dom.styles.serialize(styles));
                    }
                }
            }
        });
        editor.on('ExecCommand', (e) => {
            if (e.command === 'JustifyLeft' || e.command === 'JustifyCenter' || e.command === 'JustifyRight') {
                const node = editor.selection.getNode();
                if (editor.dom.hasClass(node, 'mce-object-video')) {
                    const parentNode = node.parentNode;
                    editor.undoManager.beforeChange();
                    editor.dom.setStyle(parentNode, 'display', 'flex');
                    if (e.command === 'JustifyLeft') {
                        editor.dom.setStyle(parentNode, 'width', 'fit-content');
                        editor.dom.setStyle(parentNode, 'justify-content', 'left');
                    } else if (e.command === 'JustifyCenter') {
                        editor.dom.setStyle(parentNode, 'width', '100%');
                        editor.dom.setStyle(parentNode, 'justify-content', 'center');
                    } else {
                        // JustifyRight
                        editor.dom.setStyle(parentNode, 'width', '100%');
                        editor.dom.setStyle(parentNode, 'justify-content', 'right');
                    }
                    editor.undoManager.add();
                }
            }
        });
        props.onInit?.(e, editor);
    };

    const getDraggedMediaInfo = (editor, rawNode) => {
        const node = rawNode || editor.selection.getNode();
        if (!node) return null;

        // VIDEO (TinyMCE object wrapper)
        const videoObj = node.closest?.('.mce-object-video');
        if (videoObj) {
            const contentId =
            videoObj.getAttribute('data-mce-p-data-target-contentid') ||
            videoObj.querySelector?.('[data-mce-p-data-target-contentid]')?.getAttribute?.(
                'data-mce-p-data-target-contentid'
            );

            if (!contentId) return null;

            return {
                type: 'video',
                contentId,
                elementSelector: `.mce-object-video[data-mce-p-data-target-contentid="${contentId}"]`,
                oldWrapper: videoObj.closest('.media-wrapper') || null,
            };
        }

        return null;
    };

    const makeMediaDragStartHandler = (editor, draggedMediaRef) => (e) => {
        const info = getDraggedMediaInfo(editor, e.target);
        draggedMediaRef.current = info;
    };

    const makeMediaDropHandler = (editor, draggedMediaRef) => () => {
        const info = draggedMediaRef.current;
  
        if (!info || info.type !== 'video') {
            draggedMediaRef.current = null;
            return;
        }

        requestAnimationFrame(() => {
            const body = editor.getBody();
            const elNow = body.querySelector(info.elementSelector);

            if (!elNow || !body.contains(elNow)) {
                draggedMediaRef.current = null;
                return;
            }

            editor.undoManager.transact(() => {
                const oldWrapper = info.oldWrapper;

                // Reuse/move old wrapper if possible
                if (oldWrapper && body.contains(oldWrapper) && !oldWrapper.contains(elNow)) {
                    const parent = elNow.parentNode;
                    if (parent) {
                        parent.insertBefore(oldWrapper, elNow);
                        oldWrapper.appendChild(elNow);
                        return;
                    }
                }

                // Create a wrapper (video and img can share wrapper rules)
                const wrapperStyle = getWrapperStyle(oldWrapper);

                const wrapper = editor.dom.create('div', {
                    class: 'media-wrapper',
                    style: wrapperStyle,
                    'data-target-content': 'true',
                    'data-target-contentid': info.contentId,
                });

                wrapper.setAttribute('contenteditable', 'false');
                wrapper.setAttribute('data-mce-resize', 'false');

                editor.dom.insertAfter(wrapper, elNow);
                wrapper.appendChild(elNow);
            });

            draggedMediaRef.current = null;
        });
    };

    const getWrapperStyle = (oldWrapper) => {
        if (!oldWrapper) {
            return 'width: 100%; max-width: 1600px; margin: 0 auto;';
        }

        // Prefer inline style if present
        const style = oldWrapper.getAttribute('style');
        if (style) return style;

        // As a fallback, compute it (covers cases where CSS was applied)
        const cs = window.getComputedStyle(oldWrapper);

        const width = cs.width && cs.width !== 'auto' ? `width: ${cs.width};` : 'width: 100%;';
        const maxWidth =
            cs.maxWidth && cs.maxWidth !== 'none'
            ? `max-width: ${cs.maxWidth};`
            : 'max-width: 1600px;';

        return `${width} ${maxWidth} margin: 0 auto;`;
    };

    return (
        <>
            <Editor
                {...props}
                onScriptsLoad={onScriptsLoad}
                onInit={onEditorInitialized}
                onEditorChange={(content, editor) => {
                    lastEditorEmittedValue.current = content;
                    props.onEditorChange?.(content, editor);
                }}
                // Skip setContent() when value came from the editor itself to prevent re-render loop
                value={
                    props.value !== undefined && props.value === lastEditorEmittedValue.current
                        ? undefined
                        : props.value
                }
                init={{
                    ...props.init,
                    content_css: [
                    'https://gemini-plus.s3.us-east-2.amazonaws.com/geminiprod/client_2/templates/delta_theme.css'
                    ],                    
                    fontsize_formats: '8pt 10pt 12pt 14pt 16pt 18pt 20pt 22pt 24pt',   
                    
                    // Preserve script, iframe (learning items, PDFs) and div[*] (media wrappers) — TinyMCE strips these by default
                    // Also preserve data-* on <a> and <button> so Learning link/button edit mode can read back attributes.
                    extended_valid_elements: [
                        'script[src|async|defer|type|charset|data-*]',
                        'iframe[src|title|class|style|width|height|allowfullscreen|frameborder|allow|sandbox|onload|data-*]',
                        'div[*]',
                        'a[href|target|class|style|data-*]',
                        'button[class|style|data-*]',
                    ].join(','),

                    // 2. Prevent stripping of "javascript:" links or protocols
                    allow_script_urls: true,

                    // 3. For modern TinyMCE versions handling embedded script text
                    xss_sanitization: false,                     
                    setup: (editor) => {
                        editorRef.current = editor;
                        editor.isInsertingSnippet = false;
                        if (props?.init?.setup) {
                            props.init.setup(editor);
                        }
                        create_icons(editor);
                        if (snippets.length > 0 && folders.length > 0) {
                            insert_snippet(editor);
                        }
                        create_snippet(editor);
                        manage_snippet(editor);
                        snippet_shortcut(editor);
                        clear_content(editor);

                        // Wire handlers (clean)
                        editor.on('dragstart', makeMediaDragStartHandler(editor, draggedMediaRef));
                        editor.on('drop', makeMediaDropHandler(editor, draggedMediaRef));
                    },
                }}
            />
            {createSnippet && <CreateDialog formAction={FormActions.Create} setSnippet={setCreateSnippet} />}
            {manageSnippet && <ManageDialog setManageSnippet={setManageSnippet} />}
            {hasContentPlugin && (
                <AddContentPlugin editor={editorRef.current} allowedDisplayTypes={allowedDisplayTypes} />
            )}
        </>
    );
};

export { TinyMCEEditor };
